import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { selectors } from '../../selectors'
import { saveQuestionResponse, submitAssessment, submitPracticeAssessment } from '../../actions'
import { trackAssessmentCompleted, trackAssessmentAutoSubmitted, trackCopyPasteAttempted, trackRightClick } from '../../../../utils/assessmentEvents'
import Completion from './completion'
import {
  AssessmentContainer,
  AssessmentContent,
  AssessmentHeader,
  AssessmentTitle,
  AssessmentTimer,
  AssessmentSubtitle,
  QuestionContainer,
  QuestionText,
  OptionsContainer,
  OptionLabel,
  RadioInput,
  OptionText,
  ButtonContainer,
  NextButton,
  BackButton,
  WarningMessage,
} from '../../Style'

const Assessment = ({setAssessment}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const questionsBehaviourArray = useSelector(selectors.getQuestions)
  const questions = questionsBehaviourArray.behaviors || []
  const currentAssessment = useSelector(
    state => state.assessment.currentAssessment
  )
  const responses = useSelector(
    state => state.assessment.currentAssessment?.responses || {}
  )
  const assessment = location.state?.assessmentData


  useEffect(() => {
    if (!assessment) {
      navigate('/assessment')
      return
    }
    
    // Set the current assessment data in Redux state
    dispatch(setCurrentAssessmentId(assessment))
  }, [assessment, navigate, dispatch])

  const isUAT = (process.env.ENVIRONMENT || '').toUpperCase() === 'UAT';

  //detect right-click and copy-paste
  useEffect(() => {
    if (isUAT) return;
    const handleRightClick = (e) => {
      trackRightClick(currentAssessment.assessment_assigned_id, currentAssessment.type, currentAssessment.isCorporate, currentAssessment.entityName);
    }
    document.addEventListener('contextmenu', handleRightClick)
    return () => {
      document.removeEventListener('contextmenu', handleRightClick)
    }
  }, [])

  useEffect(() => {
    if (isUAT) return;
    const handleCopy = (e) => {
      trackCopyPasteAttempted(currentAssessment.assessment_assigned_id, currentAssessment.type, e.clipboardData.getData('text/plain'), currentAssessment.isCorporate, currentAssessment.entityName);
    }
    document.addEventListener('copy', handleCopy)
    document.addEventListener('paste', handleCopy)
    document.addEventListener('cut', handleCopy)
    return () => {
      document.removeEventListener('copy', handleCopy)
      document.removeEventListener('paste', handleCopy)
      document.removeEventListener('cut', handleCopy)
    }
  }, [])

  const [shuffledQuestions, setShuffledQuestions] = useState([])
  const [currentPage, setCurrentPage] = useState(0)
  const [timeLeft, setTimeLeft] = useState(30 * 60) // 30 minutes in seconds
  const [showWarning, setShowWarning] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)

  // Prevent accidental exit during active assessment (reload, back button, tab close)
  useEffect(() => {
    if (isCompleted) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isCompleted])
  const [completedAssessmentData, setCompletedAssessmentData] = useState(null)
  const [unansweredQuestions, setUnansweredQuestions] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [timeUp, setTimeUp] = useState(false)
  const [assessmentStartTime, setAssessmentStartTime] = useState(null)

  const handleSubmit = async () => {
    if (!timeUp && !areAllQuestionsAnsweredOnPage()) {
      setShowWarning(true)
      scrollToFirstUnanswered()
      return
    }

    const totalQuestions = shuffledQuestions.length
    const answeredQuestions = Object.keys(responses).length

    if (answeredQuestions < totalQuestions) {
      alert(
        `Please answer all questions. ${answeredQuestions} out of ${totalQuestions} questions answered.`
      )
      return
    }

    try {
      setIsSubmitting(true) // Set loading state to true
      const assessmentDataForCompletion = {
        title: currentAssessment?.title || 'Behavioral Assessment',
        type: currentAssessment?.type || 'Behavior',
        totalQuestions: totalQuestions,
        answeredQuestions: answeredQuestions,
      }
      const timeTaken = Date.now() - assessmentStartTime.getTime()
      trackAssessmentCompleted(timeTaken, currentAssessment.assessment_assigned_id, currentAssessment.type, currentAssessment.isCorporate, currentAssessment.entityName)
      const submitAction = dispatch(submitAssessment(false))
      if (submitAction instanceof Promise) {
        await submitAction
      }
      setFinalSubmittedTime(timeLeft)
      setCompletedAssessmentData(assessmentDataForCompletion)
      setIsCompleted(true)
    } catch (error) {
      alert('Failed to submit assessment. Please try again.')
      console.error('Submit assessment error:', error)
    } finally {
      setIsSubmitting(false) // Set loading state to false
    }
  }

  const handleTimeUp = async () => {
    try {
      setTimeUp(true)
      trackAssessmentAutoSubmitted(
        currentAssessment.assessment_assigned_id, 
        null, 
        'time-up', 
        currentAssessment.type,
        currentAssessment.isCorporate, 
        currentAssessment.entityName
      )
      await handleSubmit()
      setIsCompleted(true)
    } catch (error) {
      console.error('Auto-submit error:', error)
      alert('Time is up! Assessment auto-submitted.')
      setIsCompleted(true)
    }
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer)
          handleTimeUp()
          return 0
        }
        return prevTime - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if(!assessmentStartTime){
      setAssessmentStartTime(new Date())
    }
  }, [])

  const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`
  }

  const QUESTIONS_PER_PAGE = 10

  const shuffleArray = array => {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  useEffect(() => {
    if (questions.length) {
      const allQuestions = questions.flatMap(behavior =>
        behavior.questions.map(q => ({
          ...q,
          behavior_id: behavior.behavior_id,
        }))
      )
      setShuffledQuestions(shuffleArray(allQuestions))
    }
  }, [questions])

  const startIndex = currentPage * QUESTIONS_PER_PAGE
  const endIndex = Math.min(
    startIndex + QUESTIONS_PER_PAGE,
    shuffledQuestions.length
  )
  const currentQuestions = shuffledQuestions.slice(startIndex, endIndex)

  const areAllQuestionsAnsweredOnPage = () => {
    const currentQuestions = shuffledQuestions.slice(startIndex, endIndex)
    return currentQuestions.every(
      question => responses[question.id] !== undefined
    )
  }
  const [tempHighlightedQuestions, setTempHighlightedQuestions] = useState([])

  const scrollToFirstUnanswered = () => {
    const currentQuestions = shuffledQuestions.slice(startIndex, endIndex)
    const unanswered = currentQuestions.filter(
      question => responses[question.id] === undefined
    )

    setUnansweredQuestions(unanswered.map(q => q.id))
    setTempHighlightedQuestions(unanswered.map(q => q.id))

    // Clear highlight after 3 seconds
    setTimeout(() => {
      setTempHighlightedQuestions([])
    }, 3000)

    if (unanswered.length > 0) {
      const firstUnansweredId = unanswered[0].id
      const element = document.getElementById(`question_${firstUnansweredId}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  const handleNext = () => {
    if (!areAllQuestionsAnsweredOnPage()) {
      setShowWarning(true)
      scrollToFirstUnanswered()
      return
    }
    setShowWarning(false)
    setUnansweredQuestions([])
    setCurrentPage(currentPage + 1)

    // Add scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const handleOptionSelect = (questionId, answer, pageIndex) => {
    dispatch(
      saveQuestionResponse(
        questionId,
        answer,
        currentAssessment.assessment_assigned_id
      )
    )
    setShowWarning(false)
    setUnansweredQuestions(prev => prev.filter(id => id !== questionId))

    const nextQuestion = currentQuestions[pageIndex + 1]
    const targetId = nextQuestion ? `question_${nextQuestion.id}` : null
    // Defer so the re-render after the dispatch doesn't compete with the scroll.
    setTimeout(() => {
      const el = targetId ? document.getElementById(targetId) : null
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 80)
  }

  const [finalSubmittedTime, setFinalSubmittedTime] = useState(null)

  return isCompleted ? (
    <Completion
      assessmentData={completedAssessmentData}
      submittedTime={finalSubmittedTime}
      setAssessment={setAssessment}
    />
  ) : (
    <AssessmentContainer className="exam-shell">
      <AssessmentContent className="exam-scroll">
        <AssessmentHeader>
          <AssessmentTitle>Behavioral Assessment</AssessmentTitle>
          <AssessmentTimer>Timer: {formatTime(timeLeft)}</AssessmentTimer>
        </AssessmentHeader>

        <AssessmentSubtitle>
          Page {currentPage + 1} of{' '}
          {Math.ceil(shuffledQuestions.length / QUESTIONS_PER_PAGE)}
        </AssessmentSubtitle>

        {currentQuestions.map((question, index) => (
          <QuestionContainer
            key={question.id}
            id={`question_${question.id}`}
            unanswered={tempHighlightedQuestions.includes(question.id)}
          >
            <QuestionText>
              {startIndex + index + 1}. {question.text}
            </QuestionText>
            <OptionsContainer>
              {question.options.map((option, optionIndex) => (
                <OptionLabel key={optionIndex}>
                  <RadioInput
                    type="radio"
                    name={`question_${question.id}`}
                    checked={responses[`${question.id}`] === option.text}
                    onChange={() =>
                      handleOptionSelect(`${question.id}`, option.text, index)
                    }
                  />
                  <OptionText>{option.text}</OptionText>
                </OptionLabel>
              ))}
            </OptionsContainer>
          </QuestionContainer>
        ))}

        <ButtonContainer className="exam-actions">
          <BackButton
            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            disabled={currentPage === 0}
          >
            Back
          </BackButton>
          {showWarning && (
            <WarningMessage>
              Please answer all questions on this page before proceeding.
            </WarningMessage>
          )}
          {currentPage * QUESTIONS_PER_PAGE >=
          shuffledQuestions.length - QUESTIONS_PER_PAGE ? (
            <NextButton onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </NextButton>
          ) : (
            <NextButton onClick={handleNext}>Next</NextButton>
          )}
        </ButtonContainer>
      </AssessmentContent>
    </AssessmentContainer>
  )
}

export default Assessment
